import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router';
import './Carousels.css';
import { useAuth } from "../../auth/AuthContext";
import Robotics from '../../assets/ModulesIcons/icon_Robotics.png';
import Software from '../../assets/ModulesIcons/icon_Software.png';
import Electronic from '../../assets/ModulesIcons/icon_Electronics.png';
import Science from '../../assets/ModulesIcons/icon_Science.png';
import Math from '../../assets/ModulesIcons/icon_Mechatronics.png';
import 'aos/dist/aos.css';
import AOS from 'aos';
import Announcement from './announcements/Announcement';

const Carousels = ({ settings = [] }) => {
  const [imageSlide, setImageSlide] = useState([]);
  const [modules, setModules] = useState([]);
  const [currentState, setCurrentState] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { getData } = useAuth();
  const containerRef = useRef(null);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
      mirror: true,
      offset: 50,
      disable: isMobile ? 'mobile' : false
    });
  }, [isMobile]);

  useEffect(() => {
    const fetchBackgrounds = async () => {
      try {
        setIsLoading(true);
        const [backgroundsResponse, modulesResponse] = await Promise.all([
          getData("proccess.php", "backgrounds"),
          getData("proccess.php", "modules_V2")
        ]);

        if (backgroundsResponse.data && Array.isArray(backgroundsResponse.data)) {
          setImageSlide(backgroundsResponse.data);
        } else {
          console.error("Expected an array for backgrounds, but got:", backgroundsResponse);
          setImageSlide([]);
        }

        if (modulesResponse.data && Array.isArray(modulesResponse.data)) {
          const validModules = modulesResponse.data.filter(module => 
            module && module.listing_Order > 0 && module.listing_Order < 6
          );

          const sortedModules = [...validModules].sort((a, b) => {
            const orderA = Number(a.listing_Order) || 0;
            const orderB = Number(b.listing_Order) || 0;
            return orderA - orderB;
          });
          
          setModules(sortedModules);
        } else {
          console.error("Expected an array for modules, but got:", modulesResponse);
          setModules([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setImageSlide([]);
        setModules([]);
      } finally {
        setIsLoading(false);
        setTimeout(() => AOS.refresh(), 100);
      }
    };
    fetchBackgrounds();
  }, [getData]);

  useEffect(() => {
    if (imageSlide.length > 1) {
      const interval = setInterval(() => {
        setCurrentState(prev => (prev === imageSlide.length - 1 ? 0 : prev + 1));
      }, 20000);

      return () => clearInterval(interval);
    }
  }, [imageSlide]);

  const goToPrev = () => {
    if (imageSlide.length > 0) {
      setCurrentState(prev => (prev === 0 ? imageSlide.length - 1 : prev - 1));
    }
  };

  const goToNext = () => {
    if (imageSlide.length > 0) {
      setCurrentState(prev => (prev === imageSlide.length - 1 ? 0 : prev + 1));
    }
  };

  // Safe access to settings
  const siteName = settings && settings[0] ? settings[0].site_name : 'Minds in Action';
  const shortDescription = settings && settings[0] ? settings[0].short_description : 'Default description';

  // Get icon based on module name
  const getModuleIcon = (moduleName) => {
    switch(moduleName) {
      case 'LEGO ROBOTICS': return Robotics;
      case 'CODING WITH KITTEN': return Software;
      case 'ELECTRONICS - ARDUINO MICRO-CONTORLLER': return Electronic;
      case '3D PRINTING': return Math;
      case 'SCIENCE EXPERIMENTS': return Science;
      default: return Robotics;
    }
  };

  // Get display name based on module name
  const getDisplayName = (moduleName) => {
    switch(moduleName) {
      case 'LEGO ROBOTICS': return 'ROBOTICS';
      case 'CODING WITH KITTEN': return 'Coding';
      case 'SCIENCE EXPERIMENTS': return 'SCIENCE';
      case 'ELECTRONICS - ARDUINO MICRO-CONTORLLER': return 'ELECTRONICS'
      default: return moduleName;
    }
  };

  // Get gradient style based on module name
  const getModuleStyle = (moduleName) => {
    switch(moduleName) {
      case 'LEGO ROBOTICS':
        return { background: 'linear-gradient(to right, #32a059, #57b177)' };
      case 'CODING WITH KITTEN':
        return { background: 'linear-gradient(to right, #613d9a, #7b60ab)' };
      case 'ELECTRONICS - ARDUINO MICRO-CONTORLLER':
        return { background: 'linear-gradient(to right, #f97014, #f9a662)' };
      case '3D PRINTING':
        return { background: 'linear-gradient(to right, #f9c510, #f8da61)' };
      case 'SCIENCE EXPERIMENTS':
        return { backgroundColor: '#ef4f23' };
      default:
        return { backgroundColor: '#32a059' };
    }
  };

  // Only show modules with valid names
  const validModules = modules.filter(module => {
    const validNames = [
      'LEGO ROBOTICS', 
      'CODING WITH KITTEN', 
      'ELECTRONICS - ARDUINO MICRO-CONTORLLER', 
      '3D PRINTING', 
      'SCIENCE EXPERIMENTS',
    ];
    return validNames.includes(module.module_name);
  });

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        backgroundColor: 'white',
        textAlign: 'center',
        overflow: 'hidden',
        paddingTop: '5vh'
      }}
    >
      {isLoading ? (
        <div 
          className="loading-container"
          style={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: isMobile ? '1rem' : '1.2rem'
          }}
        >
          Loading...
        </div>
      ) : (
        <>
          {imageSlide.length > 0 && imageSlide[currentState] && (
            <div 
              className="container-style"
              key={currentState}
            >
              <div
                className="image-slide active"
                style={{
                  backgroundImage: `url(https://mindsinaction.com.na/assets/media/pictures/${imageSlide[currentState].image})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  width: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1
                }}
              >
                <div className="transparent-background"></div>
                
                <div className="description">
                  {/* Left Content Card */}
                  <div 
                    className={`content-card ${isMobile ? 'mobile-card' : ''}`}
                    data-aos={isMobile ? "fade-up" : "fade-right"}
                    data-aos-delay="300"
                  >
                    <div className="text-content">
                      <h1 
                        className='primary-header' 
                        data-aos={isMobile ? "zoom-in" : "zoom-in"}
                        data-aos-delay="400"
                        style={{
                          fontSize: isMobile ? '1.8rem' : '3rem',
                          lineHeight: isMobile ? '1.2' : '1.1'
                        }}
                      >
                        {siteName}
                      </h1>
                      <div className="center-hr">
                        <hr className="hero-hr" />
                      </div>
                      <p 
                        className="description-text"
                        style={{
                          fontSize: isMobile ? '0.9rem' : '1.5rem',
                          lineHeight: isMobile ? '1.4' : '1.6'
                        }}
                      >
                        {shortDescription}
                      </p>
                    </div>
                  </div>

                  {/* Right Modules Card */}
                  <div 
                    className={`modules-card ${isMobile ? 'mobile-modules' : ''}`}
                    data-aos={isMobile ? "fade-up" : "fade-left"}
                    data-aos-delay="300"
                  >
                    <div className="module-container">
                      <h2 
                        className='modules-title'
                        data-aos="zoom-in"
                        data-aos-delay="400"
                        style={{
                          fontSize: isMobile ? '1.2rem' : '1.5rem'
                        }}
                      >
                        Our Modules
                      </h2>
                      {validModules.length > 0 ? (
                        <div className="modules-grid">
                          {validModules.map((module, idx) => (
                            <div
                              key={module.id || idx}
                              className="module-card"
                              style={getModuleStyle(module.module_name)}
                            >
                              <NavLink
                                to={`/modules/${module.categories || 'default'}/?code=${module.id}`}
                                className="module-navlink"
                              >
                                <img
                                  src={getModuleIcon(module.module_name)}
                                  alt={module.module_name}
                                  className="module-icon"
                                />
                                <h3 
                                  className='module-name'
                                  style={{
                                    fontSize: isMobile ? '0.8rem' : '1rem'
                                  }}
                                >
                                  {getDisplayName(module.module_name)}
                                </h3>
                              </NavLink>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div 
                          className="no-modules"
                          data-aos="fade-in"
                          data-aos-delay="500"
                        >
                          No modules available
                        </div>
                      )}
                    </div>
                    <Announcement />
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              {imageSlide.length > 1 && !isMobile && (
                <>
                  {/* <div
                    className="slick-arrow slick-left"
                    onClick={goToPrev}
                    data-aos="fade-right"
                    data-aos-delay="200"
                  >
                    &larr;
                  </div>
                  <div
                    className="slick-arrow slick-right"
                    onClick={goToNext}
                    data-aos="fade-left"
                    data-aos-delay="200"
                  >
                    &rarr;
                  </div> */}
                </>
              )}

              {/* Dots Indicator */}
              {/* {imageSlide.length > 1 && (
                <div 
                  className="carousel-bullets"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  {imageSlide.map((_, index) => (
                    <span
                      key={index}
                      className={`bullet ${index === currentState ? 'active' : ''}`}
                      onClick={() => setCurrentState(index)}
                    ></span>
                  ))}
                </div>
              )} */}
            </div>
          )}
          
        </>
      )}
    </div>
  );
};

export default Carousels;